import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarDays, MapPin, FileText, Video, ExternalLink, ChevronLeft, CalendarPlus, Share2, Megaphone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { api } from '../lib/api';
import { PageHeader, SectionHeader, Rich, Loading, EmptyState, formatDate } from '../components/Shared';
import { toYouTubeEmbed } from '../lib/youtube';

// Generate and download an .ics calendar file for a meeting (client-side only)
const downloadICS = (m) => {
  try {
    const date = (m.date || '').replace(/-/g, '');
    if (!date || date.length !== 8) { toast.error('This meeting has no valid date for a calendar entry.'); return; }
    const esc = (s) => (s || '').replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
    const lines = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Ward8//Meetings//EN', 'BEGIN:VEVENT',
      `UID:${m.id}@ward8`, `DTSTART;VALUE=DATE:${date}`,
      `SUMMARY:${esc(m.title)}${m.time ? esc(` (${m.time})`) : ''}`,
      m.location ? `LOCATION:${esc(m.location)}` : '',
      `DESCRIPTION:${esc(`${m.meeting_type || 'Council meeting'}. Details: ${window.location.origin}/meetings/${m.id}`)}`,
      'END:VEVENT', 'END:VCALENDAR',
    ].filter(Boolean);
    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(m.title || 'meeting').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.ics`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success('Calendar file downloaded. Open it to add the meeting to your calendar.');
  } catch {
    toast.error('Could not create the calendar file.');
  }
};

const shareMeeting = async (m) => {
  const url = `${window.location.origin}/meetings/${m.id}`;
  const data = { title: m.title, text: `${m.title} on ${formatDate(m.date)}${m.time ? ` at ${m.time}` : ''}`, url };
  try {
    if (navigator.share) { await navigator.share(data); return; }
    await navigator.clipboard.writeText(url);
    toast.success('Meeting link copied. Share it with your neighbours.');
  } catch {
    /* user cancelled share */
  }
};

const MeetingCard = ({ m }) => (
  <Card className="card-lift h-full flex flex-col" data-testid="meetings-meeting-card">
    <CardContent className="p-5 flex flex-col flex-1">
      <Link to={`/meetings/${m.id}`} className="flex-1 block">
        <div className="flex items-center gap-2 text-xs font-bold text-[#C9A84C] uppercase tracking-wider"><CalendarDays className="h-3.5 w-3.5" /> {m.meeting_type}</div>
        <p className="font-serif font-bold text-[#1a2744] text-lg mt-1.5 leading-snug">{m.title}</p>
        <p className="text-sm text-slate-600 mt-1">{formatDate(m.date)}{m.time ? ` · ${m.time}` : ''}</p>
        {m.location && <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="h-3 w-3" /> {m.location}</p>}
        {m.deputation_deadline && (
          <p className="text-xs font-semibold text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mt-2 inline-flex items-center gap-1" data-testid="meeting-deputation-deadline">
            <Megaphone className="h-3 w-3" /> Deputation deadline: {formatDate(m.deputation_deadline)}
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-3">
          {m.agenda_url && <span className="text-xs font-bold text-[#1a2744] bg-slate-100 rounded-full px-2.5 py-1 flex items-center gap-1"><FileText className="h-3 w-3" /> Agenda</span>}
          {m.minutes_url && <span className="text-xs font-bold text-[#1a2744] bg-slate-100 rounded-full px-2.5 py-1 flex items-center gap-1"><FileText className="h-3 w-3" /> Minutes</span>}
          {(m.video_url || m.livestream_url) && <span className="text-xs font-bold text-[#1a2744] bg-slate-100 rounded-full px-2.5 py-1 flex items-center gap-1"><Video className="h-3 w-3" /> Video</span>}
        </div>
      </Link>
      <div className="flex gap-2 mt-4 pt-3 border-t border-slate-100">
        <Button onClick={() => downloadICS(m)} variant="outline" className="h-11 flex-1 border-[#1a2744] text-[#1a2744] font-bold btn-press text-xs" data-testid="meeting-add-calendar-button">
          <CalendarPlus className="h-4 w-4 mr-1" /> Add to Calendar
        </Button>
        <Button onClick={() => shareMeeting(m)} variant="outline" className="h-11 w-11 p-0 border-[#1a2744] text-[#1a2744] btn-press" aria-label="Share meeting" data-testid="meeting-share-button">
          <Share2 className="h-4 w-4" />
        </Button>
      </div>
    </CardContent>
  </Card>
);

export const MeetingDetail = () => {
  const { id } = useParams();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/meetings/${id}`).then((r) => setMeeting(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Loading />;
  if (!meeting) return <EmptyState title="Meeting not found" />;

  return (
    <div>
      <PageHeader kicker={meeting.meeting_type?.toUpperCase()} title={meeting.title} subtitle={`${formatDate(meeting.date)}${meeting.time ? ` · ${meeting.time}` : ''}${meeting.location ? ` · ${meeting.location}` : ''}`} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <Link to="/meetings" className="inline-flex items-center text-sm font-bold text-[#1a2744] mb-6"><ChevronLeft className="h-4 w-4" /> All meetings</Link>

        {meeting.deputation_deadline && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex gap-3" data-testid="meeting-detail-deputation">
            <Megaphone className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900"><strong>Want to speak at this meeting?</strong> Deputation requests are due by {formatDate(meeting.deputation_deadline)}. See “How to participate” below.</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mb-8">
          {meeting.agenda_url && <Button asChild className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="meeting-agenda-button"><a href={meeting.agenda_url} target="_blank" rel="noopener noreferrer"><FileText className="h-4 w-4 mr-1.5" /> Agenda <ExternalLink className="h-3 w-3 ml-1" /></a></Button>}
          {meeting.minutes_url && <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press"><a href={meeting.minutes_url} target="_blank" rel="noopener noreferrer"><FileText className="h-4 w-4 mr-1.5" /> Minutes <ExternalLink className="h-3 w-3 ml-1" /></a></Button>}
          {meeting.livestream_url && <Button asChild variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="meeting-livestream-button"><a href={meeting.livestream_url} target="_blank" rel="noopener noreferrer"><Video className="h-4 w-4 mr-1.5" /> Watch Live <ExternalLink className="h-3 w-3 ml-1" /></a></Button>}
          <Button onClick={() => downloadICS(meeting)} variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="meeting-detail-calendar-button"><CalendarPlus className="h-4 w-4 mr-1.5" /> Add to Calendar</Button>
          <Button onClick={() => shareMeeting(meeting)} variant="outline" className="h-11 border-[#1a2744] text-[#1a2744] font-bold btn-press" data-testid="meeting-detail-share-button"><Share2 className="h-4 w-4 mr-1.5" /> Share</Button>
        </div>
        {meeting.video_url && (
          <div className="aspect-video mb-8" data-testid="meeting-video-embed"><iframe src={toYouTubeEmbed(meeting.video_url) || meeting.video_url} title="Meeting video" className="w-full h-full rounded-xl" loading="lazy" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" /></div>
        )}
        <SectionHeader kicker="PLAIN LANGUAGE" title="What this meeting is about" />
        <Card className="mb-8"><CardContent className="p-5 sm:p-8"><Rich html={meeting.summary_html} /></CardContent></Card>
        <SectionHeader kicker="HAVE YOUR SAY" title="How to participate" />
        <Card className="border-[#C9A84C] mb-6"><CardContent className="p-5 sm:p-8"><Rich html={meeting.participation_html} /></CardContent></Card>
        <Button asChild variant="outline" className="h-12 border-[#1a2744] text-[#1a2744] font-bold btn-press w-full sm:w-auto" data-testid="meeting-havesay-link">
          <Link to="/have-your-say">Full guide: How to Have Your Say</Link>
        </Button>
      </div>
    </div>
  );
};

export default function Meetings() {
  const [when, setWhen] = useState('upcoming');
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/meetings?when=${when}`).then((r) => setMeetings(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [when]);

  return (
    <div>
      <PageHeader pageKey="meetings" kicker="COUNCIL MEETING TRACKER" title="Council Meetings" subtitle="Know what Council is deciding, watch the meetings, and learn exactly how to have your say." />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <Tabs value={when} onValueChange={setWhen}>
            <TabsList className="bg-white border border-slate-200 h-12 p-1" data-testid="meeting-detail-tabs">
              <TabsTrigger value="upcoming" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="meetings-tab-upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="past" className="h-10 px-5 font-bold data-[state=active]:bg-[#1a2744] data-[state=active]:text-white" data-testid="meetings-tab-past">Past</TabsTrigger>
            </TabsList>
          </Tabs>
          <Link to="/have-your-say" className="text-sm font-bold text-[#1a2744] underline decoration-[#C9A84C] decoration-2 underline-offset-4" data-testid="meetings-havesay-link">How to attend, speak & be heard →</Link>
        </div>
        {loading ? <Loading /> : meetings.length === 0 ? (
          <EmptyState title={when === 'upcoming' ? 'No upcoming meetings posted yet' : 'No past meetings posted yet'} subtitle="Check back soon. The campaign team updates this tracker regularly." />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {meetings.map((m) => <MeetingCard key={m.id} m={m} />)}
          </div>
        )}
      </div>
    </div>
  );
}
